import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Users, ArrowLeft, Loader2, Trash2, UserPlus, ShieldCheck } from "lucide-react";

export default function Team() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "", reel_limit: 5 });

  const load = async () => {
    try {
      const r = await api.get("/auth/admin/users");
      setUsers(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.role !== "admin") navigate("/app");
  }, [user, navigate]);
  useEffect(() => { load(); }, []);

  const createUser = async (e) => {
    e.preventDefault();
    if (!form.email || form.password.length < 6) {
      toast.error("Enter an email and a password of at least 6 characters");
      return;
    }
    setCreating(true);
    try {
      await api.post("/auth/admin/users", {
        email: form.email.trim(),
        name: form.name.trim(),
        password: form.password,
        reel_limit: form.reel_limit === "" ? null : Number(form.reel_limit),
      });
      toast.success(`Account created for ${form.email}`);
      setForm({ email: "", name: "", password: "", reel_limit: 5 });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't create the account");
    } finally {
      setCreating(false);
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Delete ${u.email}? Their projects stay in the database but they lose access.`)) return;
    try {
      await api.delete(`/auth/admin/users/${u.id}`);
      toast.success("User removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't delete");
    }
  };

  const saveLimit = async (u, value) => {
    try {
      await api.patch(`/auth/admin/users/${u.id}`, { reel_limit: value === "" ? null : Number(value) });
      setUsers((cur) => cur.map((x) => (x.id === u.id ? { ...x, reel_limit: value === "" ? null : Number(value) } : x)));
    } catch (e) {
      toast.error("Couldn't update limit");
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <header className="sticky top-0 z-20 border-b border-white/10 bg-zinc-950/70 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-6">
          <button onClick={() => navigate("/app")} data-testid="team-back" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white">
            <ArrowLeft className="h-4 w-4" /> Back to studio
          </button>
          <div className="flex items-center gap-2 text-amber-500">
            <Users className="h-5 w-5" />
            <span className="font-mono2 text-xs uppercase tracking-[0.3em] text-zinc-200">Team access</span>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-12">
        {/* create */}
        <div className="rounded-2xl border border-white/10 bg-white/5 p-6 sm:p-8">
          <div className="flex items-center gap-2 text-amber-400">
            <UserPlus className="h-4 w-4" />
            <span className="font-mono2 text-[10px] uppercase tracking-[0.25em]">Invite a user</span>
          </div>
          <p className="mt-2 text-sm text-zinc-400">Accounts are invite-only — create one here and share the login with your client. They'll only see their own projects.</p>
          <form onSubmit={createUser} className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <input data-testid="new-user-email" type="email" required placeholder="Email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="h-11 rounded-xl border border-white/10 bg-zinc-900 px-4 text-sm text-white outline-none focus:border-amber-400/60" />
            <input data-testid="new-user-name" placeholder="Name (optional)" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="h-11 rounded-xl border border-white/10 bg-zinc-900 px-4 text-sm text-white outline-none focus:border-amber-400/60" />
            <input data-testid="new-user-password" type="text" required placeholder="Password (min 6)" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="h-11 rounded-xl border border-white/10 bg-zinc-900 px-4 text-sm text-white outline-none focus:border-amber-400/60" />
            <div className="flex items-center gap-2">
              <input data-testid="new-user-limit" type="number" min={0} placeholder="Reel limit" value={form.reel_limit}
                onChange={(e) => setForm({ ...form, reel_limit: e.target.value })}
                title="Max finished reels (leave blank for unlimited)"
                className="h-11 w-full rounded-xl border border-white/10 bg-zinc-900 px-4 text-sm text-white outline-none focus:border-amber-400/60" />
            </div>
            <div className="sm:col-span-2 lg:col-span-4">
              <Button data-testid="create-user-button" type="submit" disabled={creating}
                className="h-11 rounded-full border-0 bg-gradient-to-r from-amber-400 to-orange-500 px-6 font-bold text-[#0a0500] hover:shadow-[0_0_25px_rgba(245,166,35,0.4)]">
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
              </Button>
            </div>
          </form>
        </div>

        {/* list */}
        <div className="mt-10">
          <h2 className="mb-4 font-mono2 text-[10px] uppercase tracking-[0.25em] text-zinc-500">All accounts</h2>
          {loading ? (
            <div className="flex items-center gap-2 text-zinc-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-white/10">
              <table className="w-full text-left text-sm" data-testid="users-table">
                <thead className="bg-white/5 text-[11px] uppercase tracking-wider text-zinc-500">
                  <tr>
                    <th className="px-5 py-3">User</th>
                    <th className="px-5 py-3">Role</th>
                    <th className="px-5 py-3">Reels</th>
                    <th className="px-5 py-3">Limit</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {users.map((u) => (
                    <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-white/[0.02]">
                      <td className="px-5 py-4">
                        <div className="font-medium text-white">{u.name || u.email.split("@")[0]}</div>
                        <div className="text-xs text-zinc-500">{u.email}</div>
                      </td>
                      <td className="px-5 py-4">
                        {u.role === "admin" ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/30 bg-amber-400/10 px-2.5 py-1 text-[11px] font-semibold text-amber-400">
                            <ShieldCheck className="h-3 w-3" /> Admin
                          </span>
                        ) : (
                          <span className="rounded-full border border-white/10 px-2.5 py-1 text-[11px] text-zinc-400">User</span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-zinc-300">{u.reel_count ?? 0}</td>
                      <td className="px-5 py-4">
                        {u.role === "admin" ? (
                          <span className="text-zinc-500">∞</span>
                        ) : (
                          <input
                            data-testid={`limit-${u.id}`}
                            type="number" min={0}
                            defaultValue={u.reel_limit ?? ""}
                            placeholder="∞"
                            onBlur={(e) => saveLimit(u, e.target.value)}
                            className="h-9 w-20 rounded-lg border border-white/10 bg-zinc-900 px-3 text-sm text-white outline-none focus:border-amber-400/60"
                          />
                        )}
                      </td>
                      <td className="px-5 py-4 text-right">
                        {u.role !== "admin" && (
                          <button data-testid={`delete-user-${u.id}`} onClick={() => removeUser(u)} className="text-zinc-500 transition-colors hover:text-red-400">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
